import React, { useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  useApplyShareGrants,
  type ShareLevel,
  type ShareResourceType,
  type StagedShare,
} from "src/api/shares";
import { NucToast } from "src/reusable/components/Toast";
import GroupMemberPicker, {
  type SearchUser,
} from "src/views/admin/groups/GroupMemberPicker";
import { useGroups } from "src/views/admin/groups/groupService";
import PermissionSelect, { labelForLevel } from "./PermissionSelect";

interface StagedShareListProps {
  /** Gates the user-search query, like the share dialog's own picker. */
  open: boolean;
  value: StagedShare[];
  onChange: (next: StagedShare[]) => void;
  tid: string;
  /** One line above the pickers saying what happens until the shares apply. */
  note: string;
}

/**
 * Grants chosen before the resource exists: the same people/groups pickers
 * and permission levels as the share dialog, held locally until the caller
 * creates the resource and applies them (`useStagedShares`).
 */
const StagedShareList: React.FC<StagedShareListProps> = ({
  open,
  value,
  onChange,
  tid,
  note,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
  const [userLevel, setUserLevel] = useState<ShareLevel>("read");
  const [selectedGroup, setSelectedGroup] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [groupQuery, setGroupQuery] = useState("");
  const [groupLevel, setGroupLevel] = useState<ShareLevel>("read");
  const { data: groupsData } = useGroups();

  const stagedUserIds = useMemo(
    () => value.filter((s) => s.kind === "user").map((s) => s.id),
    [value],
  );
  const stagedGroupIds = useMemo(
    () => new Set(value.filter((s) => s.kind === "group").map((s) => s.id)),
    [value],
  );
  const availableGroups = useMemo(
    () => (groupsData?.items || []).filter((g) => !stagedGroupIds.has(g.id)),
    [groupsData, stagedGroupIds],
  );

  const stageUser = () => {
    if (!selectedUser) return;
    onChange([
      ...value,
      {
        kind: "user",
        id: selectedUser.id,
        label: selectedUser.user_display_name || selectedUser.username,
        level: userLevel,
      },
    ]);
    setSelectedUser(null);
    setUserLevel("read");
  };

  const stageGroup = () => {
    if (!selectedGroup) return;
    onChange([
      ...value,
      {
        kind: "group",
        id: selectedGroup.id,
        label: selectedGroup.name,
        level: groupLevel,
      },
    ]);
    setSelectedGroup(null);
    setGroupQuery("");
    setGroupLevel("read");
  };

  const unstage = (share: StagedShare) =>
    onChange(
      value.filter((s) => !(s.kind === share.kind && s.id === share.id)),
    );

  return (
    <Box data-testid={`${tid}-staged`}>
      <FormHelperText sx={{ mb: 1 }}>{note}</FormHelperText>
      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2, minHeight: 36 }}
      >
        <Tab
          data-testid={`${tid}-staged-tab-people`}
          icon={<PersonIcon fontSize="small" />}
          iconPosition="start"
          label="People"
          sx={{ minHeight: 36 }}
        />
        <Tab
          data-testid={`${tid}-staged-tab-groups`}
          icon={<GroupIcon fontSize="small" />}
          iconPosition="start"
          label="Groups"
          sx={{ minHeight: 36 }}
        />
      </Tabs>
      {activeTab === 0 && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <GroupMemberPicker
            open={open}
            excludeIds={stagedUserIds}
            value={selectedUser}
            onChange={setSelectedUser}
            label="Search users by name or username"
            textFieldTestId={`${tid}-staged-user-search`}
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <PermissionSelect
              value={userLevel}
              onChange={setUserLevel}
              testId={`${tid}-staged-user-permission`}
            />
            <Button
              variant="contained"
              size="small"
              onClick={stageUser}
              disabled={!selectedUser}
              data-testid={`${tid}-staged-user-add-btn`}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      )}
      {activeTab === 1 && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Autocomplete
            options={availableGroups}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedGroup}
            onChange={(_e, v) => setSelectedGroup(v)}
            inputValue={groupQuery}
            onInputChange={(_e, v) => setGroupQuery(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search groups by name"
                size="small"
                data-testid={`${tid}-staged-group-search`}
              />
            )}
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <PermissionSelect
              value={groupLevel}
              onChange={setGroupLevel}
              testId={`${tid}-staged-group-permission`}
            />
            <Button
              variant="contained"
              size="small"
              onClick={stageGroup}
              disabled={!selectedGroup}
              data-testid={`${tid}-staged-group-add-btn`}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      )}
      {value.length > 0 && (
        <List dense disablePadding data-testid={`${tid}-staged-list`}>
          {value.map((share) => (
            <ListItem
              key={`${share.kind}-${share.id}`}
              sx={{ px: 0 }}
              data-testid={`${tid}-staged-${share.kind}-${share.id}`}
            >
              <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                {share.kind === "user" ? (
                  <PersonIcon fontSize="small" />
                ) : (
                  <GroupIcon fontSize="small" />
                )}
              </Avatar>
              <ListItemText
                primary={share.label}
                secondaryTypographyProps={{ component: "div" }}
                secondary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={labelForLevel(share.level)}
                      variant="outlined"
                    />
                    <Typography variant="caption">
                      Applied when the agent is created
                    </Typography>
                  </Stack>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => unstage(share)}
                  data-testid={`${tid}-staged-remove-${share.kind}-${share.id}`}
                  aria-label={`Remove ${share.label}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

/**
 * Staged grants for a create form, applied once the resource exists. Creation
 * never fails on sharing: grants that do not land are named in a toast and the
 * created resource goes to `onFailed`, which should open a share dialog that
 * outlives the form.
 */
export const useStagedShares = <T extends { id: number; name: string }>(
  resourceType: ShareResourceType,
  onFailed: (created: T) => void,
) => {
  const [staged, setStaged] = useState<StagedShare[]>([]);
  const applyGrants = useApplyShareGrants(resourceType);
  const appliedForRef = useRef<number | null>(null);

  /** Applies the staged grants to a just-created resource, once per id.
   *  Resolves true when every grant landed, false when one failed or the id
   *  was already handled. */
  const applyAfterCreate = async (created: T): Promise<boolean> => {
    if (appliedForRef.current === created.id) return false;
    appliedForRef.current = created.id;
    const shares = staged;
    setStaged([]);
    if (shares.length === 0) return true;
    const { failed } = await applyGrants(created.id, shares);
    if (failed.length === 0) return true;
    NucToast({
      message: `Couldn't share "${created.name}" with ${failed
        .map((f) => f.label)
        .join(", ")}. Try again from Manage access.`,
      type: "error",
      autoClose: 6000,
      closeButton: true,
    });
    onFailed(created);
    return false;
  };

  return { staged, setStaged, applyAfterCreate };
};

export default StagedShareList;
